import type { ControlDto, DashboardDto, DataModelState, DataTypeDto, KpiDto, ProposalDto, RequiredDocumentDto, RequiredDocumentsState, Site, TaskDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, Card, PageTitle } from '../../ui';
import { rowInLevel, SiteChooser, useLevels, visibleIn } from './Livelli';
import { useSections } from './SectionQuestions';
import { Headline, KpiChart } from './KpiChart';
import { KpiBuilder } from './KpiBuilder';

/** Le voci dei documenti obbligatori a cui manca qualcosa. */
const MISSING: ReadonlyArray<RequiredDocumentDto['stato']> = ['da_caricare', 'incompleto', 'da_rinnovare'];
/** Voci mostrate in ogni riquadro: le altre si vedono nella pagina. */
const SHOWN = 5;

const n = (v: number) => v.toLocaleString('it-IT');

/** Un numero da guardare, con il link alla pagina che lo spiega. */
function Tile({ href, label, value, note, tone = 'normal' }: { href: string; label: string; value: number | null; note: string; tone?: 'normal' | 'alert' }) {
  const alert = tone === 'alert' && !!value;
  return (
    <a href={href} className={`block rounded-xl border bg-white p-4 shadow-sm transition hover:border-accent/50 hover:shadow ${alert ? 'border-amber-300' : 'border-black/[0.06]'}`}>
      <span className="block text-xs font-medium text-ink/55">{label}</span>
      <span className={`mt-1 block text-2xl font-semibold tabular-nums ${alert ? 'text-amber-700' : 'text-ink'}`}>{value === null ? '…' : n(value)}</span>
      <span className="mt-1 block text-xs text-ink/50">{note}</span>
    </a>
  );
}

/** Un riquadro con un elenco breve e il link a tutto. */
function Panel({ title, href, more, children }: { title: string; href: string; more?: string; children: ReactNode }) {
  return (
    <Card className="!p-5">
      <div className="mb-3 flex items-baseline gap-2">
        <h2 className="min-w-0 flex-1 text-sm font-semibold">{title}</h2>
        <a href={href} className="shrink-0 text-xs font-medium text-accent hover:underline">
          {more ?? 'Apri'} →
        </a>
      </div>
      {children}
    </Card>
  );
}

/** Un indicatore nella dashboard: a cosa serve, l'ultimo valore, il grafico, la fonte e i comandi per spostarlo o toglierlo. */
function KpiCard({ k, onUp, onDown, onHide, onDelete }: { k: KpiDto; onUp?: () => void; onDown?: () => void; onHide: () => void; onDelete?: () => void }) {
  const lib = k.libreria;
  const tool = 'grid h-7 w-7 place-items-center rounded-md text-ink/40 hover:bg-black/5 hover:text-ink disabled:opacity-30';
  return (
    <Card className="!p-5">
      <div className="flex items-start gap-1">
        <h3 className="min-w-0 flex-1 text-sm font-semibold">{k.name}</h3>
        <button type="button" className={tool} disabled={!onUp} onClick={onUp} title="Sposta prima" aria-label="Sposta prima">
          ↑
        </button>
        <button type="button" className={tool} disabled={!onDown} onClick={onDown} title="Sposta dopo" aria-label="Sposta dopo">
          ↓
        </button>
        <button type="button" className={tool} onClick={onHide} title="Togli dalla mia dashboard" aria-label="Togli dalla mia dashboard">
          ✕
        </button>
      </div>
      {lib && <p className="mt-0.5 text-xs text-ink/55">{lib.a_cosa_serve}</p>}
      {!lib && k.visibilita !== 'mia' && <p className="mt-0.5 text-xs text-ink/55">{k.visibilita === 'tutti' ? 'Nella dashboard di tutti' : 'Nella dashboard dei ruoli scelti'}</p>}
      <div className="mt-2">
        <Headline k={k} />
      </div>
      <div className="mt-2">
        <KpiChart k={k} />
      </div>
      {lib && (
        <p className="mt-2 text-[11px] text-ink/45" title={[lib.fonte.testo, lib.nota].filter(Boolean).join(' ')}>
          {lib.fonte.base === 'prassi' ? 'Buona prassi' : lib.fonte.base === 'standard' ? 'Standard del settore' : 'Norma'}: {lib.fonte.testo}
        </p>
      )}
      {onDelete && (
        <button type="button" className="mt-2 text-xs text-ink/45 hover:text-red-700" onClick={() => confirm(`Eliminare il grafico «${k.name}»?`) && onDelete()}>
          elimina il grafico
        </button>
      )}
    </Card>
  );
}

/**
 * Gli indicatori della dashboard di chi guarda (ADR 0213 § 5): nell'ordine che ha scelto, con quelli tolti da rimettere,
 * quelli senza dati in fondo con cosa serve, e il costruttore per crearne di nuovi.
 */
function KpiSection({ livello }: { livello: string }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const board = useTenantQuery<DashboardDto>(['dashboard', livello], `/dashboard?livello=${livello}`);
  const [building, setBuilding] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'dashboard'] });
  const saveLayout = useMutation({
    mutationFn: (body: { ordine: string[]; nascosti: string[] }) => api(t('/dashboard'), { method: 'PUT', body: { livello, ...body } }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => api(t(`/kpis/${id}/remove`), { method: 'POST' }), onSuccess: refresh });
  const href = (path: string) => `#/t/${tenant.id}/${path}`;
  const all = (board.data?.visibili ?? []).filter((k) => !k.errore);
  const ids = all.map((k) => k.id);
  const hidden = (board.data?.nascosti ?? []).map((x) => x.id);
  const shown = all.filter((k) => !k.manca?.length && k.serie.length > 0);
  const toDo = all.filter((k) => k.manca?.length);
  const move = (id: string, by: -1 | 1) => {
    // Lo spostamento è fra i grafici visibili: quello vicino nella griglia.
    const i = shown.findIndex((k) => k.id === id);
    const other = shown[i + by];
    if (!other) return;
    const order = [...ids];
    const a = order.indexOf(id);
    const b = order.indexOf(other.id);
    [order[a], order[b]] = [order[b]!, order[a]!];
    saveLayout.mutate({ ordine: order, nascosti: hidden });
  };
  const hide = (id: string) => saveLayout.mutate({ ordine: ids.filter((x) => x !== id), nascosti: [...hidden, id] });
  const restore = (id: string) => saveLayout.mutate({ ordine: [...ids, id], nascosti: hidden.filter((x) => x !== id) });
  if (!board.data) return null;
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-baseline gap-2">
        <h2 className="min-w-0 flex-1 text-sm font-semibold uppercase tracking-wide text-ink/50">Indicatori</h2>
        {!building && (
          <Button variant="ghost" className="!px-2 !py-1 text-xs" onClick={() => setBuilding(true)}>
            + Nuovo grafico
          </Button>
        )}
        <a href={href('indicatori')} className="shrink-0 text-xs font-medium text-accent hover:underline">
          Tutti →
        </a>
      </div>
      {building && (
        <div className="mb-4">
          <KpiBuilder
            onCancel={() => setBuilding(false)}
            onSaved={() => {
              setBuilding(false);
              refresh();
            }}
          />
        </div>
      )}
      {shown.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          {shown.map((k, i) => (
            <KpiCard
              key={k.id}
              k={k}
              onUp={i > 0 ? () => move(k.id, -1) : undefined}
              onDown={i < shown.length - 1 ? () => move(k.id, 1) : undefined}
              onHide={() => hide(k.id)}
              onDelete={!k.libreria && (k.mio || isManager) ? () => remove.mutate(k.id) : undefined}
            />
          ))}
        </div>
      )}
      {!shown.length && !toDo.length && !building && <p className="text-sm text-ink/55">Nessun grafico in questa dashboard: creane uno con «Nuovo grafico».</p>}
      {toDo.length > 0 && (
        <Card className="mt-4 !p-5">
          <h3 className="text-sm font-semibold">Da completare per vederli</h3>
          <p className="mb-2 text-xs text-ink/55">Questi indicatori non mostrano numeri finché mancano i dati: niente stime.</p>
          <ul className="divide-y divide-black/[0.05] text-sm">
            {toDo.map((k) => (
              <li key={k.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1.5">
                <span className="font-medium">{k.name}</span>
                <span className="min-w-0 flex-1 text-xs text-ink/55">
                  servono{' '}
                  {k.manca!.map((m, i) => (
                    <span key={m.tipo}>
                      {i > 0 && ', '}
                      <a href={href(`e/${m.tipo}`)} className="text-accent hover:underline">
                        {m.nome}
                      </a>
                      {m.motivo === 'campi_vuoti' ? ` (con ${m.campi.map((c) => c.replace(/_/g, ' ')).join(' e ')})` : ''}
                    </span>
                  ))}
                </span>
                <button type="button" className="text-xs text-ink/40 hover:text-ink" onClick={() => hide(k.id)}>
                  togli
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {board.data.nascosti.length > 0 && (
        <p className="mt-3 text-xs text-ink/55">
          Tolti dalla tua dashboard:{' '}
          {board.data.nascosti.map((x, i) => (
            <span key={x.id}>
              {i > 0 && ' · '}
              {x.name}{' '}
              <button type="button" className="text-accent hover:underline" onClick={() => restore(x.id)}>
                rimetti
              </button>
            </span>
          ))}
        </p>
      )}
    </section>
  );
}

/**
 * La pagina d'ingresso del gestionale (ADR 0212): a colpo d'occhio ciò che c'è e ciò che aspetta, per tutta l'azienda o
 * per la sede scelta. Mostra solo numeri che il sistema ha già; ogni riquadro porta alla pagina che li spiega.
 */
export function DashboardPage() {
  const { tenant, isManager, me } = useTenant();
  const levels = useLevels();
  const site = levels.site;
  // Ciò che non ha una sede (task, proposte, controlli, indicatori) sta nel livello dell'azienda.
  const companyLevel = !site;
  const sections = useSections();
  const model = useTenantQuery<DataModelState>(['data-model'], '/data-model');
  const required = useTenantQuery<RequiredDocumentsState>(['archive', 'required'], '/archive/required');
  const sitesQ = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const scope = isManager ? 'open' : 'mine';
  const tasks = useTenantQuery<{ data: TaskDto[] }>(['tasks', scope], `/tasks?scope=${scope}`, { enabled: companyLevel });
  const proposals = useTenantQuery<{ data: ProposalDto[] }>(['proposals'], '/proposals', { enabled: companyLevel && isManager });
  const controls = useTenantQuery<{ data: ControlDto[] }>(['controls'], '/controls', { enabled: companyLevel });
  // Gli indicatori del livello (ADR 0213 § 4): nell'azienda i suoi e quelli delle sedi divisi per sede, in una sede i suoi.
  const livello = site?.id ?? 'azienda';
  const href = (path: string) => `#/t/${tenant.id}/${path}`;

  // I documenti del livello: dentro una sede quelli della sede, nell'azienda quelli dell'azienda (come l'archivio).
  const siteOf = (d: RequiredDocumentDto) => (d.ambito.ambito === 'sedi' ? d.ambito.sedi[0]! : null);
  const docs = (required.data?.data ?? []).filter((d) => !levels.multi || siteOf(d) === (site?.id ?? null));
  const missing = docs.filter((d) => MISSING.includes(d.stato));
  const toConfirmDocs = docs.filter((d) => d.stato === 'da_confermare');

  // I tipi del livello, come nel menu, con le voci del livello.
  const types = (model.data?.data ?? []).filter((x) => x.stato === 'attivo' && !sections.tipi.get(x.chiave)?.parte_di && visibleIn(levels, x.chiave, !!site));
  const count = (x: DataTypeDto) => x.entita.filter((e) => rowInLevel(levels, e.legami)).length;
  const pending = types.filter((x) => sections.tipi.get(x.chiave)?.da_confermare);
  const empty = types.filter((x) => !count(x));

  const siteProposals = (sitesQ.data?.data ?? []).filter((x) => x.status === 'da_confermare').length;
  const openProposals = (proposals.data?.data ?? []).filter((p) => p.status === 'aperta').length;
  const exceptions = (controls.data?.data ?? []).reduce((sum, c) => sum + (c.enabled ? c.open_exceptions : 0), 0);
  const place = site ? [site.address, site.municipality, site.province && `(${site.province})`].filter(Boolean).join(' ') : '';

  return (
    <div className="space-y-6">
      <PageTitle
        title={site ? site.name : tenant.name}
        subtitle={
          site
            ? `Sede di ${tenant.name}${place ? ` · ${place}` : ''}. Ciò che c’è in questa sede e ciò che aspetta.`
            : levels.multi
              ? `Ciao ${me.name.split(' ')[0]}: qui c’è ciò che vale per tutta l’azienda. Entra in una sede per i suoi dati.`
              : `Ciao ${me.name.split(' ')[0]}: ciò che c’è nell’azienda e ciò che aspetta.`
        }
      />
      <SiteChooser />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {companyLevel && <Tile href={href('oggi')} label={isManager ? 'Task aperti' : 'I miei task'} value={tasks.data ? tasks.data.data.length : null} note="Le cose da fare" />}
        <Tile href={href('archivio')} label="Documenti da caricare" value={required.data ? missing.length : null} note={toConfirmDocs.length ? `e ${n(toConfirmDocs.length)} da confermare` : 'Obbligatori mancanti o da rinnovare'} tone="alert" />
        <Tile href={href(pending[0] ? `e/${pending[0].chiave}` : 'dashboard')} label="Sezioni da confermare" value={sections.state ? pending.length : null} note="Preparate con l’ipotesi più ampia" tone="alert" />
        {companyLevel && <Tile href={href('controlli')} label="Eccezioni aperte" value={controls.data ? exceptions : null} note="Dai controlli di gestione" tone="alert" />}
        <Tile href={href(empty[0] ? `e/${empty[0].chiave}` : 'dashboard')} label="Elenchi da riempire" value={model.data && sections.state ? empty.length : null} note={`Su ${n(types.length)} tipi di dati`} />
        {site && <Tile href={href(`sedi/${site.id}`)} label="Attività nella sede" value={site.attivita_in_sede.filter((a) => a.stato !== 'esclusa').length} note="Scheda della sede" />}
      </div>

      {isManager && companyLevel && (siteProposals > 0 || openProposals > 0) && (
        <div className="flex flex-wrap gap-2">
          {siteProposals > 0 && (
            <a href={href('sedi')} className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 hover:bg-amber-100">
              {siteProposals === 1 ? '1 sede proposta da scegliere' : `${n(siteProposals)} sedi proposte da scegliere`} →
            </a>
          )}
          {openProposals > 0 && (
            <a href={href('proposte')} className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 hover:bg-amber-100">
              {openProposals === 1 ? '1 proposta di miglioramento' : `${n(openProposals)} proposte di miglioramento`} →
            </a>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {missing.length > 0 && (
          <Panel title="Documenti che mancano" href={href('archivio')} more={missing.length > SHOWN ? `Tutti (${n(missing.length)})` : 'Archivio'}>
            <ul className="space-y-1.5 text-sm">
              {missing.slice(0, SHOWN).map((d) => (
                <li key={d.id} className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate">{d.titolo}</span>
                  <span className="shrink-0 text-xs text-ink/50">{d.stato === 'da_rinnovare' ? 'da rinnovare' : d.mancanti > 1 ? `${n(d.mancanti)} mancanti` : 'da caricare'}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )}
        {pending.length > 0 && (
          <Panel title="Sezioni da confermare" href={href(`e/${pending[0]!.chiave}`)} more="Comincia">
            <p className="mb-2 text-xs text-ink/55">Le ha preparate il sistema con l’ipotesi più ampia: aprile per rispondere alle domande.</p>
            <ul className="flex flex-wrap gap-1.5">
              {pending.map((x) => (
                <li key={x.chiave}>
                  <a href={href(`e/${x.chiave}`)} className="block rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800 hover:bg-amber-100">
                    {x.etichetta}
                  </a>
                </li>
              ))}
            </ul>
          </Panel>
        )}
        {companyLevel && tasks.data && tasks.data.data.length > 0 && (
          <Panel title={isManager ? 'Task aperti' : 'I miei task'} href={href('oggi')} more={tasks.data.data.length > SHOWN ? `Tutti (${n(tasks.data.data.length)})` : 'Oggi'}>
            <ul className="space-y-1.5 text-sm">
              {tasks.data.data.slice(0, SHOWN).map((x) => (
                <li key={x.id} className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate">{x.title}</span>
                  <span className="shrink-0 text-xs text-ink/50">{x.assignee?.name ?? 'da assegnare'}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>

      <KpiSection livello={livello} />

    </div>
  );
}
